import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { TRANSCRIPT_SOURCES, coverageGap, getMeetingTranscript, reportMeeting, updateMeeting } from '@mkcore/crm/schema';
import { Badge, Button, Card, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, Textarea } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { Markdown } from '@/components/markdown';
import { MarkdownEditor } from '@/components/markdown-editor';
import { MeetingDialog, MeetingStatusBadge, meetingQuery, meetingWhen, type Meeting } from '@/features/crm/meetings';

export const Route = createFileRoute('/_app/crm/meetings/$id')({ component: MeetingPage });

/**
 * One meeting (CO-0584): when and with whom, the transcript (the parts of Plaud recordings, or from Teams or pasted)
 * and the report. The report routine (CO-0840) fills the same fields through crm.meeting.update and crm.meeting.report.
 */
function MeetingPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const message = useErrorMessage();
  const canUpdate = useCan('crm:update');
  const q = useQuery(meetingQuery(id));
  const [editing, setEditing] = useState(false);
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-96" />;
  const m = q.data;
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-[9rem_1fr] gap-2 text-sm">
      <dt className="text-fg-3">{label}</dt>
      <dd>{children}</dd>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow={t('meetings.title')}
        title={m.title}
        description={meetingWhen(m)}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MeetingStatusBadge status={m.status} />
            {canUpdate && (
              <Button variant="secondary" onClick={() => setEditing(true)}>
                <Pencil /> {t('common.edit')}
              </Button>
            )}
          </div>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        <div className="grid content-start gap-4">
          <Card className="p-4">
            <h2 className="mb-3 text-lg font-bold">{t('meetings.details')}</h2>
            <dl className="grid gap-2">
              <Row label={t('meetings.fields.organisation')}>
                {m.organisationId ? (
                  <Link to="/org/$id" params={{ id: m.organisationId }} className="underline">
                    {m.organisationName}
                  </Link>
                ) : (
                  '–'
                )}
              </Row>
              {m.leadId && (
                <Row label={t('meetings.fields.lead')}>
                  <Link to="/crm/leads/$id" params={{ id: m.leadId }} className="underline">
                    {t('meetings.openLead')}
                  </Link>
                </Row>
              )}
              <Row label={t('meetings.participants')}>
                {m.participants.length === 0 ? (
                  t('meetings.noParticipants')
                ) : (
                  <ul aria-label={t('meetings.participants')}>
                    {m.participants.map((p) => (
                      <li key={p.id}>
                        {p.name}
                        {p.speaker && <span className="text-fg-3"> · {p.speaker}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </Row>
            </dl>
          </Card>
          <Transcript meeting={m} canUpdate={canUpdate} />
        </div>
        <Report meeting={m} canUpdate={canUpdate} />
      </div>
      {editing && <MeetingDialog meeting={m} onClose={() => setEditing(false)} />}
    </>
  );
}

const clock = (ms: number) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * The parts of Plaud recordings that belong to the meeting (CO-1366), in time order, and on request their transcript
 * per segment with time and speaker. The report routine links them; the rest of a recording is not here.
 */
function RecordingParts({ meeting: m }: { meeting: Meeting }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const q = useQuery({ queryKey: ['crm', 'meeting', m.id, 'transcript', m.version], queryFn: () => call(getMeetingTranscript, { id: m.id }), enabled: show });
  if (!m.recordings.length) return null;
  return (
    <div className="mb-3 grid gap-2 text-sm">
      <ul aria-label={t('meetings.recordings')} className="grid gap-1">
        {m.recordings.map((r) => (
          <li key={`${r.recordingId}-${r.startMs}`}>
            <span className="font-bold">{r.name || r.sourceId}</span>{' '}
            <span className="tabular text-fg-3">{t('meetings.part', { at: formatDateTime(r.at), from: clock(r.startMs), to: clock(r.endMs) })}</span>
          </li>
        ))}
      </ul>
      <div>
        <Button size="sm" variant="ghost" onClick={() => setShow((v) => !v)}>
          {show ? t('meetings.hideTranscript') : t('meetings.showTranscript')}
        </Button>
      </div>
      {show &&
        (!q.data ? (
          <Skeleton className="h-24" />
        ) : (
          <ol aria-label={t('meetings.transcript')} className="max-h-96 overflow-auto rounded-md bg-sunken p-3 text-xs">
            {q.data.parts.flatMap((p) =>
              p.segments.map((x) => (
                <li key={`${p.recordingId}-${x.startMs}-${x.speaker}`} className="mb-1">
                  <span className="tabular text-fg-3" title={formatDateTime(x.at)}>{clock(x.startMs)}</span> <span className="font-bold">{x.speaker}</span>: {x.text}
                </li>
              )),
            )}
          </ol>
        ))}
    </div>
  );
}

/** The transcript: the parts of Plaud recordings, or one from elsewhere with its source, a reference and the text (kept as a file). */
function Transcript({ meeting: m, canUpdate }: { meeting: Meeting; canUpdate: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [source, setSource] = useState<(typeof TRANSCRIPT_SOURCES)[number]>(m.transcriptSource ?? 'upload');
  const [ref, setRef] = useState(m.transcriptRef ?? '');
  const [text, setText] = useState('');
  const [error, setError] = useState<string>();
  const save = useActionMutation(updateMeeting, { invalidate: [['crm']], silentErrors: true, success: t('meetings.transcriptSaved'), onSuccess: () => (setText(''), setError(undefined)) });
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-lg font-bold">{t('meetings.transcript')}</h2>
      <RecordingParts meeting={m} />
      {m.recordings.length > 0 ? null : m.transcriptSource ? (
        <p className="mb-3 text-sm">
          {t(`meetings.source.${m.transcriptSource}`)}
          {m.transcriptRef && <span className="text-fg-3"> · {m.transcriptRef}</span>}
          {m.transcriptFileId && (
            <>
              {' · '}
              <a href={`/api/v1/files/${m.transcriptFileId}/content`} target="_blank" rel="noreferrer" className="underline">
                {t('meetings.openTranscript')}
              </a>
            </>
          )}
        </p>
      ) : (
        <p className="mb-3 text-sm text-fg-3">{t('meetings.noTranscript')}</p>
      )}
      {canUpdate && (
        <form
          className="grid gap-2"
          aria-label={t('meetings.saveTranscript')}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(
              { input: { id: m.id, transcript: { source, ...(ref.trim() && { ref: ref.trim() }), ...(text.trim() && { text }) } }, ifMatch: m.version },
              { onError: (err) => setError(message(err)) },
            );
          }}
        >
          <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
            <Field label={t('meetings.fields.source')}>
              {(p) => (
                <NativeSelect {...p} value={source} onChange={(e) => setSource(e.target.value as never)}>
                  {TRANSCRIPT_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {t(`meetings.source.${s}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('meetings.fields.ref')} hint={t('meetings.refHint')}>
              {(p) => <Input {...p} value={ref} onChange={(e) => setRef(e.target.value)} />}
            </Field>
          </div>
          <Field label={t('meetings.fields.text')} hint={m.transcriptFileId ? t('meetings.textReplaces') : undefined}>
            {(p) => <Textarea {...p} rows={5} value={text} onChange={(e) => setText(e.target.value)} />}
          </Field>
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <div>
            <Button type="submit" size="sm" variant="secondary" loading={save.isPending}>
              {t('meetings.saveTranscript')}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

/** How much of the recording parts the report covers (#1289): a badge, and the gaps when it covers too little. */
function Coverage({ coverage: c }: { coverage: NonNullable<Meeting['coverage']> }) {
  const { t } = useTranslation();
  const gap = coverageGap(c);
  return (
    <div className="mb-3 grid gap-1 text-sm">
      <Badge tone={gap ? 'warning' : 'success'} className="w-fit">
        {t(gap ? 'meetings.coverage.gap' : 'meetings.coverage.ok', { percent: c.percent })}
      </Badge>
      {gap && c.gaps.length > 0 && (
        <ul aria-label={t('meetings.coverage.gaps')} className="text-xs text-fg-3">
          {c.gaps.map((g) => (
            <li key={`${g.recordingId}:${g.startMs}`} className="tabular">
              {t('meetings.coverage.range', { from: clock(g.startMs), to: clock(g.endMs) })}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The report in markdown; recording it makes the meeting reported, and the routines that follow start from there. */
function Report({ meeting: m, canUpdate }: { meeting: Meeting; canUpdate: boolean }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [draft, setDraft] = useState(m.report);
  const [editing, setEditing] = useState(!m.report);
  const [error, setError] = useState<string>();
  useEffect(() => setDraft(m.report), [m.report]);
  const record = useActionMutation(reportMeeting, { invalidate: [['crm']], silentErrors: true, success: t('meetings.reported'), onSuccess: () => (setEditing(false), setError(undefined)) });
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">{t('meetings.report')}</h2>
        {m.reportedAt && <span className="text-xs text-fg-3">{t('meetings.reportedAt', { at: formatDateTime(m.reportedAt) })}</span>}
      </div>
      {m.report && m.coverage && <Coverage coverage={m.coverage} />}
      {m.report && !editing && (
        <>
          <Markdown text={m.report} className="text-sm" />
          {canUpdate && (
            <Button className="mt-3" size="sm" variant="secondary" onClick={() => setEditing(true)}>
              <Pencil /> {t('meetings.editReport')}
            </Button>
          )}
        </>
      )}
      {!m.report && !canUpdate && <p className="text-sm text-fg-3">{t('meetings.noReport')}</p>}
      {canUpdate && editing && (
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            record.mutate({ input: { id: m.id, report: draft } }, { onError: (err) => setError(message(err)) });
          }}
        >
          <MarkdownEditor aria-label={t('meetings.report')} placeholder={t('meetings.reportHint')} value={draft} onChange={setDraft} rows={14} />
          {error && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={!draft.trim()} loading={record.isPending}>
              {t('meetings.recordReport')}
            </Button>
            {m.report && (
              <Button type="button" size="sm" variant="secondary" onClick={() => (setDraft(m.report), setEditing(false))}>
                {t('common.cancel')}
              </Button>
            )}
          </div>
        </form>
      )}
    </Card>
  );
}
